import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { Zone, ZoneResponse } from '../types/zone.type'

@Injectable()
export class ZoneService {
  constructor(private _http: HttpClient) {}

  /**
   * Gets the zones for an event
   * @param {number} organizationId
   * @param {number} eventId
   * @returns {Observable<Zone[]>}
   */
  public getZones(organizationId: number, eventId: number, perPage: number = 10, page: number = 1): Observable<ZoneResponse> {
    const params = new HttpParams().set('per_page', perPage).set('page', page)
    return this._http.get<ZoneResponse>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/zones`, { params })
  }

  /**
   * Creates zones for an event.
   * Body shape: `{ zones: [{ name, max_capacity, weight }, ...] }`.
   * @param {number} organizationId
   * @param {number} eventId
   * @param {Omit<Zone, 'id' | 'slug'>[]} zones
   * @returns {Observable<ZoneResponse>}
   */
  public createZones(organizationId: number, eventId: number, zones: Omit<Zone, 'id' | 'slug'>[]): Observable<ZoneResponse> {
    return this._http.post<ZoneResponse>(
      `${environment.planningApi}organizations/${organizationId}/events/${eventId}/zones`,
      { zones },
      { context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true) }
    )
  }

  /**
   * Deletes a zone for an event.
   * @param {number} organizationId
   * @param {number} eventId
   * @param {number} zoneId
   * @returns {Observable<void>}
   */
  public deleteZone(organizationId: number, eventId: number, zoneId: number): Observable<void> {
    return this._http.delete<void>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/zones/${zoneId}`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Bulk-updates zones for an event.
   * @param {number} organizationId
   * @param {number} eventId
   * @param {Array<Omit<Zone, 'slug'> & { id: number }>} zones
   * @returns {Observable<ZoneResponse>}
   */
  public updateZones(organizationId: number, eventId: number, zones: Array<Omit<Zone, 'slug'> & { id: number }>): Observable<ZoneResponse> {
    return this._http.patch<ZoneResponse>(
      `${environment.planningApi}organizations/${organizationId}/events/${eventId}/zones`,
      { zones },
      { context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true) }
    )
  }
}
